'use client'
import { useState, useEffect } from 'react'
import Link from 'next/link'
import { AdminHomeIcon, LocationIcon, ContactIcon, EmailIcon } from '@/components/icons'
import { ContactInfo, TypeSection } from '@/entities'

interface FooterData {
  contactInfo?: ContactInfo
  propertyTypes?: Array<{
    name: string
    href: string
  }>
}

const quickLinks = [
  { name: 'Home', href: '/' },
  { name: 'Properties', href: '/properties' },
  { name: 'About Us', href: '/about' },
  { name: 'Contact', href: '/contact' },
]

// Fallback data
const fallbackPropertyTypes = [
  { name: 'Condos', href: '/properties?type=condo' },
  { name: 'Houses', href: '/properties?type=house' },
  { name: 'Offices', href: '/properties?type=office' },
  { name: 'Commercial', href: '/properties?type=commercial' },
]

const fallbackContactInfo: ContactInfo = {
  companyName: 'PropertyMS',
  description: 'Your trusted partner in finding the perfect property. We specialize in premium real estate across Thailand.',
  phone: ['+66 (0) 2-345-6789'],
  email: ['info@property.com'],
  address: '123 Sukhumvit Road\nBangkok, Thailand 10110',
}

/**
 * Custom hook to fetch footer data from contact and homepage content
 */
const useFooterData = (): { data: FooterData; loading: boolean } => {
  const [data, setData] = useState<FooterData>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        // Fetch contact and homepage content in parallel
        const [contactResponse, homepageResponse] = await Promise.all([
          fetch('/api/admin/content/contact'),
          fetch('/api/admin/content/homepage'),
        ])

        let contactInfo: ContactInfo | undefined
        let propertyTypes: Array<{ name: string; href: string }> | undefined

        // Process contact data
        if (contactResponse.ok) {
          const contactData = await contactResponse.json()
          contactInfo = contactData.content?.contactInfo
        }

        // Process homepage data for property types
        if (homepageResponse.ok) {
          const homepageData = await homepageResponse.json()
          const typeSection: TypeSection | undefined = homepageData.content?.typeSection
          
          if (typeSection?.items) {
            propertyTypes = typeSection.items.map(item => ({
              name: item.title,
              href: `/properties?type=${item.buildingType.toLowerCase()}`,
            }))
          }
        }

        setData({ contactInfo, propertyTypes })
      } catch (error) {
        console.error('Failed to fetch footer data:', error)
        // Data will remain empty, fallbacks will be used
      } finally {
        setLoading(false)
      }
    }

    fetchFooterData()
  }, [])

  return { data, loading }
}

/**
 * Website footer with links, contact info, and social media
 */
export default function Footer() {
  const currentYear = new Date().getFullYear()
  const { data, loading } = useFooterData()

  // Use dynamic data with fallbacks
  const contactInfo = data.contactInfo || fallbackContactInfo
  const propertyTypes = data.propertyTypes || fallbackPropertyTypes
  const companyName = contactInfo.companyName || 'PropertyMS'
  const description = contactInfo.description || fallbackContactInfo.description
  const address = contactInfo.address || fallbackContactInfo.address
  const phone = contactInfo.phone?.[0] || fallbackContactInfo.phone![0]
  const email = contactInfo.email?.[0] || fallbackContactInfo.email![0]

  // Create social links from contact info
  const socialLinks = []
  if (contactInfo.facebook) {
    socialLinks.push({
      name: 'Facebook',
      href: contactInfo.facebook,
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
    })
  }
  if (contactInfo.instagram) {
    socialLinks.push({
      name: 'Instagram',
      href: contactInfo.instagram,
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
        </svg>
      ),
    })
  }
  if (contactInfo.x) {
    socialLinks.push({
      name: 'X (Twitter)',
      href: contactInfo.x,
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
    })
  }
  if (contactInfo.lineId) {
    socialLinks.push({
      name: 'LINE',
      href: `https://line.me/ti/p/${contactInfo.lineId}`,
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 10.304c0-5.369-5.383-9.738-12-9.738S0 4.935 0 10.304c0 4.823 4.279 8.878 10.06 9.608.391.085.92.258 1.055.594.123.305.08.784.04 1.095l-.172 1.027c-.052.31-.242 1.21 1.061.659 1.303-.55 7.031-4.135 9.602-7.083 1.766-1.926 2.354-3.875 2.354-5.9" />
        </svg>
      ),
    })
  }
  if (contactInfo.whatsapp) {
    socialLinks.push({
      name: 'WhatsApp',
      href: `https://wa.me/${contactInfo.whatsapp.replace(/[^\d]/g, '')}`,
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
        </svg>
      ),
    })
  }

  // Show loading state
  if (loading) {
    return (
      <footer className="bg-neutral text-neutral-content">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-4">
                <div className="h-6 bg-neutral-focus rounded animate-pulse"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-neutral-focus rounded animate-pulse"></div>
                  <div className="h-4 bg-neutral-focus rounded animate-pulse w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </footer>
    )
  }

  return (
    <footer className="bg-neutral text-neutral-content">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <AdminHomeIcon className="w-6 h-6" />
              <h3 className="text-xl font-bold">{companyName}</h3>
            </div>
            <p className="text-sm opacity-80">
              {description}
            </p>
            {socialLinks.length > 0 && (
              <div className="flex space-x-4">
                {socialLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-ghost btn-sm btn-circle hover:bg-neutral-focus"
                    aria-label={link.name}
                  >
                    {link.icon}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link 
                    href={link.href}
                    className="link link-hover text-sm opacity-80 hover:opacity-100"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Property Types */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold">Property Types</h4>
            <ul className="space-y-2">
              {propertyTypes.map((type) => (
                <li key={type.href}>
                  <Link 
                    href={type.href}
                    className="link link-hover text-sm opacity-80 hover:opacity-100"
                  >
                    {type.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold">Contact Info</h4>
            <div className="space-y-3 text-sm">
              {address && (
                <div className="flex items-start space-x-2">
                  <LocationIcon className="w-4 h-4 mt-0.5" />
                  <span className="opacity-80">
                    {address.split('\n').map((line, idx) => (
                      <span key={idx}>
                        {line}
                        {idx < address.split('\n').length - 1 && <br />}
                      </span>
                    ))}
                  </span>
                </div>
              )}
              {phone && (
                <div className="flex items-center space-x-2">
                  <ContactIcon className="w-4 h-4" />
                  <a 
                    href={`tel:${phone.replace(/\s/g, '')}`}
                    className="link link-hover opacity-80 hover:opacity-100"
                  >
                    {phone}
                  </a>
                </div>
              )}
              {email && (
                <div className="flex items-center space-x-2">
                  <EmailIcon className="w-4 h-4" />
                  <a 
                    href={`mailto:${email}`}
                    className="link link-hover opacity-80 hover:opacity-100"
                  >
                    {email}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="divider my-8"></div>
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="text-sm opacity-70">
            © {currentYear} {companyName}. All rights reserved.
          </div>
          <div className="flex space-x-6 text-sm">
            <Link href="/privacy-policy" className="link link-hover opacity-70 hover:opacity-100">
              Privacy Policy
            </Link>
            <Link href="/terms-of-service" className="link link-hover opacity-70 hover:opacity-100">
              Terms of Service
            </Link>
            <Link href="/cookie-policy" className="link link-hover opacity-70 hover:opacity-100">
              Cookie Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}